import axios from 'axios';
import { http } from '@silvertours/front-shared';
import { I18nLegacy } from '@silvertours/front-legacy-shared';
import { useSiteInfo } from '@silvertours/front-entities';

import { addItemToSearchHistory } from '../SearchHistory/utils';
import { SearchFormState, useSearchContext } from '../context';

import { useSearch } from './useSearch';
import { useAffiliate } from './useAffiliate';

const searchEndpoint = 'v1/quotes';

/** Sort key mietwagen honours for the recommender default-sort experiment (DI-134). */
const RECOMMENDER_DEFAULT_SORT = 'ranking';

const mergeQueryParams = (...queries: Array<string | undefined>) => {
  const merged = new URLSearchParams();
  const seen = new Set<string>();

  queries
    .filter((query): query is string => Boolean(query))
    .forEach(query => {
      new URLSearchParams(query).forEach((value, key) => {
        const pair = `${key}=${value}`;
        if (!seen.has(pair)) {
          seen.add(pair);
          merged.append(key, value);
        }
      });
    });

  return merged.toString();
};

const appendQueryParams = (baseUrl: string, params: string) => {
  if (!params) {
    return baseUrl;
  }

  const url = new URL(baseUrl, window.location.href);
  new URLSearchParams(params).forEach((value, key) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

export interface SearchError {
  error: {
    name:
      | 'request_invalid'
      | 'form_validation_error'
      | 'validation_error'
      | 'search_unavailable';
    meta?: {
      errors: Record<string, string> | Array<string>;
    };
  };
}

const createPayload = (
  {
    affiliate,
    customerAge,
    provider,
    depDate,
    depLocation,
    destDate,
    destLocation,
    inputValue,
  }: SearchFormState,
  isInternalCall: boolean,
  includeDefaultSort: boolean,
) => {
  const shouldSearchServiceNumbers =
    isInternalCall && inputValue.departure.startsWith('#');
  return shouldSearchServiceNumbers
    ? {
        customerAge,
        provider,
        depDate,
        depLocation: { name: inputValue.departure },
        destDate,
      }
    : {
        aff: affiliate,
        customerAge,
        provider,
        depDate,
        depLocation,
        destDate,
        destLocation,
        // Recommender default-sort experiment (DI-134): present only for the
        // recommender variant, so control and non-participants are unchanged.
        ...(includeDefaultSort && { defaultSort: RECOMMENDER_DEFAULT_SORT }),
      };
};

export const matchErrors = (axiosError: http.AxiosError) => {
  const errorData = axiosError.response?.data as SearchError;
  const errors = errorData?.error?.meta?.errors;

  const result = [];

  if (
    errorData &&
    ['validation_error', 'form_validation_error'].includes(
      errorData.error?.name,
    )
  ) {
    if (errors && Array.isArray(errors)) {
      // TODO: why is this also a locationError?
      result.push({ type: 'locationError', message: errors[0] });
      return result;
    }

    if (errors?.depDate) {
      result.push({ type: 'dateError', message: errors.depDate });
    } else if (errors?.destDate) {
      result.push({ type: 'dateError', message: errors.destDate });
    }

    if (errors?.depLocation) {
      result.push({ type: 'locationError', message: errors.depLocation });
    } else if (errors?.destLocation) {
      result.push({ type: 'locationError', message: errors.destLocation });
    }

    if (errors?.customerAge) {
      result.push({ type: 'ageError', message: errors.customerAge });
    }
  }

  if (!result.length) {
    result.push({ type: 'axiosError', message: axiosError.message });
  }

  return result;
};

export const matchError = (axiosError: http.AxiosError) =>
  matchErrors(axiosError)[0];

/** Only `redirectUrl` is read; the endpoint returns more that nothing consumes. */
type SearchSubmitResponse = {
  redirectUrl?: string;
};

export const useSubmitHandler = () => {
  const { formatApiUrl } = useSearch();
  const { redirectToTradedoubler } = useAffiliate();
  const { prefilterQuery, isInternalCall } = useSearchContext();
  const { language, market } = I18nLegacy.useLocale();
  const { featureToggles } = useSiteInfo();

  return async (
    formState: SearchFormState | null,
    queryParams: string,
    onSuccess?: (payload: SearchSubmitResponse) => void,
    onError?: (error: http.AxiosError) => void,
    openInNewWindow = false,
  ) => {
    if (!formState) {
      return;
    }

    const payload = createPayload(
      formState,
      isInternalCall,
      featureToggles.defaultSortRecommender,
    );

    try {
      const { data } = await http.axiosInstance.post<SearchSubmitResponse>(
        formatApiUrl(searchEndpoint),
        payload,
      );

      if (onSuccess) {
        onSuccess(data);
      }

      if (data.redirectUrl) {
        if (formState.depLocation) {
          addItemToSearchHistory({
            depLocation: formState.depLocation,
            destLocation: formState.destLocation ?? undefined,
            depDate: formState.depDate.split('T')[0],
            depTime: formState.depDate.split('T')[1],
            destDate: formState.destDate.split('T')[0],
            destTime: formState.destDate.split('T')[1],
            depWeekDay: '',
            destWeekDay: '',
            imageUrl: '',
            language: language || '',
            market: market || '',
          });
        }

        const url = appendQueryParams(
          data.redirectUrl,
          mergeQueryParams(queryParams, prefilterQuery),
        );

        const tdUrl = redirectToTradedoubler(url);
        const targetUrl = tdUrl || url;
        if (openInNewWindow) {
          window.open(targetUrl, '_blank');
        } else {
          window.location.assign(targetUrl);
        }
      }
    } catch (axiosError: unknown) {
      if (axiosError && axios.isAxiosError(axiosError)) {
        console.error(
          `Request error when fetching ${searchEndpoint}`,
          axiosError.message,
          payload,
        );
        if (onError) {
          onError(axiosError);
        } else {
          throw new Error(axiosError.message, { cause: axiosError });
        }
      }
    }
  };
};
